import { useQueries } from "@tanstack/react-query";

import {
    getTrendsTable,
    trendsTablePath,
    getTrendsSwitchGraph,
    trendsSwitchGraphPath,
} from "app/pages/industry-analysis/api-client/marketTrendsApi";
import type { ICategoryComparison } from "app/pages/industry-analysis/categories-context/CategoriesContext";
import { getMarketSharesQueryParamsForCategory } from "app/pages/industry-analysis/players/market-shares/utils/utils";
import type { TrendsTableResult, TrendsSwitchGraphResult } from "app/pages/industry-analysis/queries/marketTrends";
import DurationServiceSingleton from "app/services/DurationService";
import { getAverage } from "app/UtilitiesAndConstants/UtilityFunctions/statistics";

const TABLE_ROWS_LIMIT = 5;

export type PlayerData = {
    playerName: string;
    playerIcon: string;
    marketShare: number;
    previousPeriodMarketShare?: number;
    change?: number;
    color: string;
};

export type TopPlayersComparisonRow = {
    playerRank: number;
    primaryCategory: PlayerData | null;
    comparedCategory: PlayerData | null;
};

type TopPlayersComparisonData = {
    data: TopPlayersComparisonRow[];
    categories: ICategoryComparison[];
    isPeriodOverPeriod: boolean;
    isLoading: boolean;
    isError: boolean;
};

/**
 * Extracts previous period averages from switch graph data for a specific category
 */
const getPreviousPeriodAverages = (
    switchGraphData: TrendsSwitchGraphResult,
    _categoryKey: string,
): Record<string, number> => {
    if (!("compareData" in switchGraphData)) {
        return {};
    }

    return Object.fromEntries(
        Object.entries(switchGraphData.compareData).map(([domain, domainData]) => {
            const average = getAverage(domainData.total?.map(({ value }) => ({ y: value.share }))) ?? 0;
            return [domain, average];
        }),
    );
};

/**
 * Selects top players data from table results, optionally enriched with previous period data
 */
const selectTopPlayersData = (
    { data }: TrendsTableResult,
    categoryColor: string,
    previousPeriodData?: Record<string, number>,
): PlayerData[] => {
    return data.slice(0, TABLE_ROWS_LIMIT).map((item) => {
        const basePlayerData = {
            marketShare: item.share,
            playerName: item.domain,
            playerIcon: item.favicon,
            color: categoryColor,
        };

        if (previousPeriodData) {
            const previousShare = previousPeriodData[item.id] ?? 0;
            return {
                ...basePlayerData,
                previousPeriodMarketShare: previousShare,
                change: item.share - previousShare,
            };
        }

        return basePlayerData;
    });
};

const combinePlayersData = (
    primaryData: PlayerData[] | undefined,
    comparedData: PlayerData[] | undefined,
): TopPlayersComparisonRow[] => {
    return Array.from({ length: TABLE_ROWS_LIMIT }, (_, index) => ({
        playerRank: index + 1,
        primaryCategory: primaryData?.[index] || null,
        comparedCategory: comparedData?.[index] || null,
    }));
};

export const useTopPlayersComparisonData = (categories: ICategoryComparison[]): TopPlayersComparisonData => {
    const [primaryCategory, comparedCategory] = categories;
    const isPeriodOverPeriod = DurationServiceSingleton.isPeriodOverPeriod();

    /**
     * Query configuration factory for table queries
     */
    const createTableQuery = (category: ICategoryComparison["category"], color: string) => {
        const queryParams = getMarketSharesQueryParamsForCategory(category);
        return {
            queryKey: [trendsTablePath, queryParams] as const,
            queryFn: () => getTrendsTable(queryParams),
            meta: { categoryColor: color, categoryKey: category.forApi },
        };
    };

    /**
     * Query configuration factory for switch graph queries (period over period only)
     */
    const createSwitchGraphQuery = (category: ICategoryComparison["category"]) => {
        const queryParams = getMarketSharesQueryParamsForCategory(category);
        return {
            queryKey: [trendsSwitchGraphPath, queryParams] as const,
            queryFn: () => getTrendsSwitchGraph(queryParams),
            enabled: isPeriodOverPeriod,
            meta: { categoryKey: category.forApi },
        };
    };

    /**
     * Build queries array based on period over period state
     */
    const queries = [
        createTableQuery(primaryCategory.category, primaryCategory.color),
        createTableQuery(comparedCategory.category, comparedCategory.color),
        ...(isPeriodOverPeriod
            ? [createSwitchGraphQuery(primaryCategory.category), createSwitchGraphQuery(comparedCategory.category)]
            : []),
    ];

    const queryResults = useQueries({
        queries,
        combine: (results) => {
            const [primaryTableQuery, comparedTableQuery, primarySwitchQuery, comparedSwitchQuery] = results;

            // Extract previous period data if available
            const primaryPreviousPeriodData =
                isPeriodOverPeriod && primarySwitchQuery?.data
                    ? getPreviousPeriodAverages(
                          primarySwitchQuery.data as TrendsSwitchGraphResult,
                          primaryCategory.category.forApi,
                      )
                    : undefined;

            const comparedPreviousPeriodData =
                isPeriodOverPeriod && comparedSwitchQuery?.data
                    ? getPreviousPeriodAverages(
                          comparedSwitchQuery.data as TrendsSwitchGraphResult,
                          comparedCategory.category.forApi,
                      )
                    : undefined;

            // Transform table data with optional previous period enrichment
            const primaryData = primaryTableQuery.data
                ? selectTopPlayersData(
                      primaryTableQuery.data as TrendsTableResult,
                      primaryCategory.color,
                      primaryPreviousPeriodData,
                  )
                : undefined;

            const comparedData = comparedTableQuery.data
                ? selectTopPlayersData(
                      comparedTableQuery.data as TrendsTableResult,
                      comparedCategory.color,
                      comparedPreviousPeriodData,
                  )
                : undefined;

            // Calculate loading and error states
            const requiredQueries = isPeriodOverPeriod ? results : results.slice(0, 2);
            const isLoading = requiredQueries.some((query) => query.isLoading);
            const isError = requiredQueries.some((query) => query.isError);

            return {
                data: combinePlayersData(primaryData, comparedData),
                categories,
                isPeriodOverPeriod,
                isLoading,
                isError,
            };
        },
    });

    return queryResults;
};
